import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@shared/lib/apiClient';
import { useActiveOrganization } from '@shared/context/ActiveOrganizationContext';
import { primaryMonthFromPeriod, sortedYmMonthsFromPeriod } from './periodUtils';
function scopeParams(scope) {
    if (scope === 'all')
        return {};
    return { userId: scope };
}
/** Params extra (accountId/range) a partir do filtro. */
function filterParams(filter) {
    const params = {};
    if (filter?.accountId)
        params.accountId = filter.accountId;
    if (filter?.range)
        params.range = `${filter.range.start},${filter.range.end}`;
    return params;
}
/** Fragmento serializado do filtro para o queryKey. */
function filterKey(filter) {
    return [
        filter?.accountId ?? null,
        filter?.range ? `${filter.range.start},${filter.range.end}` : null,
    ];
}
export const useCategoriesReport = (scope, period, filter) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: [
            'dashboard',
            activeOrganizationId,
            'categories-report',
            scope,
            period,
            ...filterKey(filter),
        ],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/dashboard/categories-report', { params: { ...scopeParams(scope), period, ...filterParams(filter) } });
            return data;
        },
    });
};
export const useDashboardSummary = (scope, period, filter) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: [
            'dashboard',
            activeOrganizationId,
            'summary',
            scope,
            period,
            ...filterKey(filter),
        ],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/dashboard/summary', {
                params: { ...scopeParams(scope), period, ...filterParams(filter) },
            });
            return data;
        },
    });
};
export const useTopTransactions = (scope, period, filter, limit = 5) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: [
            'dashboard',
            activeOrganizationId,
            'top-transactions',
            scope,
            period,
            limit,
            ...filterKey(filter),
        ],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/dashboard/top-transactions', {
                params: {
                    ...scopeParams(scope),
                    period,
                    limit: String(limit),
                    ...filterParams(filter),
                },
            });
            return data;
        },
    });
};
/** Com um único mês no picker: janela de `1 + N` meses `[M−N … M]` em `/dashboard/evolution`. */
export const DASHBOARD_EVOLUTION_SINGLE_ANCHOR_PAST_MONTHS = 5;
function evolutionQueryKey(organizationId, scope, params, filter) {
    const suffix = filterKey(filter);
    if ('periods' in params) {
        return [
            'dashboard',
            organizationId,
            'evolution',
            scope,
            'periods',
            params.periods.join(','),
            ...suffix,
        ];
    }
    if ('anchor' in params) {
        return [
            'dashboard',
            organizationId,
            'evolution',
            scope,
            'range',
            params.anchor,
            params.pastMonths ?? 0,
            params.futureMonths ?? 0,
            ...suffix,
        ];
    }
    return ['dashboard', organizationId, 'evolution', scope, 'legacy', params.months, ...suffix];
}
/** A partir do `period` do dashboard: ≥2 meses → `periods`; senão janela âncora + meses anteriores. */
export function evolutionParamsFromDashboardPeriod(period) {
    const months = sortedYmMonthsFromPeriod(period);
    if (months.length >= 2) {
        return { periods: months };
    }
    const anchor = months.length === 1 ? months[0] : primaryMonthFromPeriod(period);
    return {
        anchor,
        pastMonths: DASHBOARD_EVOLUTION_SINGLE_ANCHOR_PAST_MONTHS,
        futureMonths: 0,
    };
}
export const useEvolutionReport = (scope, params, filter) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: evolutionQueryKey(activeOrganizationId, scope, params, filter),
        enabled: orgQueryReady,
        queryFn: async () => {
            const base = { ...scopeParams(scope), ...filterParams(filter) };
            let query;
            if ('periods' in params) {
                query = { ...base, periods: params.periods.join(',') };
            }
            else if ('anchor' in params) {
                query = {
                    ...base,
                    anchor: params.anchor,
                    pastMonths: String(params.pastMonths ?? 0),
                    futureMonths: String(params.futureMonths ?? 0),
                    months: '6',
                };
            }
            else {
                query = { ...base, months: String(params.months) };
            }
            const { data } = await apiClient.get('/dashboard/evolution', {
                params: query,
            });
            return data;
        },
    });
};
export const useProjections = (scope) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['dashboard', activeOrganizationId, 'projections', scope],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/dashboard/projections', {
                params: scopeParams(scope),
            });
            return data;
        },
    });
};
export const useRecalculateDashboard = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (scope) => {
            const userId = scope === 'all' ? undefined : scope;
            const { data } = await apiClient.post('/dashboard/recalculate', {
                userId,
            });
            return data;
        },
        onSuccess: async () => {
            if (activeOrganizationId) {
                await queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
            }
        },
    });
};
